import Database from 'better-sqlite3';
import path from 'path';
import os from 'os';
import fs from 'fs';
import type { 
  DeployedService, 
  ServiceEventRecord, 
  OperationResult,
  ServiceFilter,
  ServiceStatus,
  ServiceEvent
} from '../types.js';

export class RegistryDatabase {
  private db: Database.Database;

  constructor(dbPath?: string) {
    // 默认路径：~/.codebox/registry.db
    const defaultPath = path.join(os.homedir(), '.codebox', 'registry.db');
    const finalPath = dbPath || defaultPath;

    // 确保目录存在
    const dir = path.dirname(finalPath);
    if (!fs.existsSync(dir)) {
      fs.mkdirSync(dir, { recursive: true });
    }

    this.db = new Database(finalPath);
    this.db.pragma('journal_mode = WAL');
    this.initSchema();
  }

  private initSchema(): void {
    // 服务表
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS services (
        id TEXT PRIMARY KEY,
        project_id TEXT NOT NULL,
        server_id TEXT NOT NULL,
        deployment_id TEXT NOT NULL,
        name TEXT NOT NULL,
        type TEXT NOT NULL,
        host TEXT NOT NULL,
        port INTEGER,
        url TEXT NOT NULL,
        health_check_config TEXT,
        status TEXT NOT NULL,
        version TEXT NOT NULL,
        deployed_at TEXT NOT NULL,
        last_health_check TEXT,
        last_status_change TEXT,
        metrics TEXT,
        metadata TEXT,
        tags TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      );

      CREATE INDEX IF NOT EXISTS idx_services_status ON services(status);
      CREATE INDEX IF NOT EXISTS idx_services_project ON services(project_id);
      CREATE INDEX IF NOT EXISTS idx_services_server ON services(server_id);
    `);

    // 事件表
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS service_events (
        id TEXT PRIMARY KEY,
        service_id TEXT NOT NULL,
        event TEXT NOT NULL,
        timestamp TEXT NOT NULL,
        details TEXT,
        FOREIGN KEY (service_id) REFERENCES services(id) ON DELETE CASCADE
      );

      CREATE INDEX IF NOT EXISTS idx_events_service ON service_events(service_id);
      CREATE INDEX IF NOT EXISTS idx_events_timestamp ON service_events(timestamp);
    `);

    // 操作历史表
    this.db.exec(`
      CREATE TABLE IF NOT EXISTS operation_history (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        service_id TEXT NOT NULL,
        operation TEXT NOT NULL,
        success BOOLEAN NOT NULL,
        message TEXT,
        error TEXT,
        timestamp TEXT NOT NULL,
        duration INTEGER,
        FOREIGN KEY (service_id) REFERENCES services(id) ON DELETE CASCADE
      );

      CREATE INDEX IF NOT EXISTS idx_operations_service ON operation_history(service_id);
      CREATE INDEX IF NOT EXISTS idx_operations_timestamp ON operation_history(timestamp);
    `);
  }

  // 服务管理
  registerService(service: DeployedService): void {
    const stmt = this.db.prepare(`
      INSERT OR REPLACE INTO services (
        id, project_id, server_id, deployment_id, name, type, host, port, url,
        health_check_config, status, version, deployed_at, last_health_check,
        last_status_change, metrics, metadata, tags, updated_at
      ) VALUES (
        ?, ?, ?, ?, ?, ?, ?, ?, ?,
        ?, ?, ?, ?, ?,
        ?, ?, ?, ?, CURRENT_TIMESTAMP
      )
    `);

    stmt.run(
      service.id,
      service.projectId,
      service.serverId,
      service.deploymentId,
      service.name,
      service.type,
      service.host,
      service.port || null,
      service.url,
      JSON.stringify(service.healthCheck || null),
      service.status,
      service.version,
      service.deployedAt,
      service.lastHealthCheck || null,
      service.lastStatusChange || null,
      JSON.stringify(service.metrics || null),
      JSON.stringify(service.metadata || null),
      JSON.stringify(service.tags || null)
    );

    this.recordEvent({
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      serviceId: service.id,
      event: ServiceEvent.Registered,
      timestamp: new Date().toISOString(),
      details: { version: service.version }
    });
  }

  getService(id: string): DeployedService | null {
    const row = this.db.prepare('SELECT * FROM services WHERE id = ?').get(id) as any;
    return row ? this.rowToService(row) : null;
  }

  listServices(filter?: ServiceFilter): DeployedService[] {
    let query = 'SELECT * FROM services WHERE 1=1';
    const params: any[] = [];

    if (filter) {
      if (filter.status) {
        query += ' AND status = ?';
        params.push(filter.status);
      }
      if (filter.type) {
        query += ' AND type = ?';
        params.push(filter.type);
      }
      if (filter.projectId) {
        query += ' AND project_id = ?';
        params.push(filter.projectId);
      }
      if (filter.serverId) {
        query += ' AND server_id = ?';
        params.push(filter.serverId);
      }
    }

    query += ' ORDER BY deployed_at DESC';

    const rows = this.db.prepare(query).all(...params) as any[];
    return rows.map(row => this.rowToService(row));
  }

  updateServiceStatus(id: string, status: ServiceStatus): void {
    const stmt = this.db.prepare(`
      UPDATE services 
      SET status = ?, last_status_change = ?, updated_at = CURRENT_TIMESTAMP
      WHERE id = ?
    `);

    const timestamp = new Date().toISOString();
    stmt.run(status, timestamp, id);

    this.recordEvent({
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      serviceId: id,
      event: ServiceEvent.StatusChanged,
      timestamp,
      details: { newStatus: status }
    });
  }

  updateServiceMetrics(id: string, metrics: any): void {
    const stmt = this.db.prepare(`
      UPDATE services 
      SET metrics = ?, updated_at = CURRENT_TIMESTAMP
      WHERE id = ?
    `);

    stmt.run(JSON.stringify(metrics), id);
  }

  updateLastHealthCheck(id: string, timestamp: string): void {
    const stmt = this.db.prepare(`
      UPDATE services 
      SET last_health_check = ?, updated_at = CURRENT_TIMESTAMP
      WHERE id = ?
    `);

    stmt.run(timestamp, id);
  }

  deleteService(id: string): void {
    this.db.prepare('DELETE FROM services WHERE id = ?').run(id);
  }

  // 事件管理
  recordEvent(event: ServiceEventRecord): void {
    const stmt = this.db.prepare(`
      INSERT INTO service_events (id, service_id, event, timestamp, details)
      VALUES (?, ?, ?, ?, ?)
    `);

    stmt.run(
      event.id,
      event.serviceId,
      event.event,
      event.timestamp,
      JSON.stringify(event.details || null)
    );
  }

  getServiceEvents(serviceId: string, limit = 100): ServiceEventRecord[] {
    const rows = this.db.prepare(`
      SELECT * FROM service_events 
      WHERE service_id = ? 
      ORDER BY timestamp DESC 
      LIMIT ?
    `).all(serviceId, limit) as any[];

    return rows.map(row => ({
      id: row.id,
      serviceId: row.service_id,
      event: row.event,
      timestamp: row.timestamp,
      details: row.details ? JSON.parse(row.details) : undefined
    }));
  }

  // 操作历史
  recordOperation(result: OperationResult): void {
    const stmt = this.db.prepare(`
      INSERT INTO operation_history (
        service_id, operation, success, message, error, timestamp, duration
      ) VALUES (?, ?, ?, ?, ?, ?, ?)
    `);

    stmt.run(
      result.serviceId,
      result.operation,
      result.success ? 1 : 0,
      result.message || null,
      result.error || null,
      result.timestamp,
      result.duration || null
    );
  }

  getOperationHistory(serviceId: string, limit = 50): OperationResult[] {
    const rows = this.db.prepare(`
      SELECT * FROM operation_history 
      WHERE service_id = ? 
      ORDER BY timestamp DESC 
      LIMIT ?
    `).all(serviceId, limit) as any[];

    return rows.map(row => ({
      serviceId: row.service_id,
      operation: row.operation,
      success: row.success === 1,
      message: row.message || undefined,
      error: row.error || undefined,
      timestamp: row.timestamp,
      duration: row.duration || undefined
    }));
  }

  // 统计信息
  getStatistics() {
    const stats = this.db.prepare(`
      SELECT 
        COUNT(*) as total,
        SUM(CASE WHEN status = 'running' THEN 1 ELSE 0 END) as running,
        SUM(CASE WHEN status = 'stopped' THEN 1 ELSE 0 END) as stopped,
        SUM(CASE WHEN status = 'unhealthy' THEN 1 ELSE 0 END) as unhealthy,
        SUM(CASE WHEN status = 'unknown' THEN 1 ELSE 0 END) as unknown
      FROM services
    `).get() as any;

    return {
      total: stats.total || 0,
      running: stats.running || 0,
      stopped: stats.stopped || 0,
      unhealthy: stats.unhealthy || 0,
      unknown: stats.unknown || 0
    };
  }

  // 清理旧数据
  cleanupOldData(retentionDays: number): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
    const cutoffTimestamp = cutoffDate.toISOString();

    // 清理旧事件
    this.db.prepare(`
      DELETE FROM service_events 
      WHERE timestamp < ?
    `).run(cutoffTimestamp);

    // 清理旧操作历史
    this.db.prepare(`
      DELETE FROM operation_history 
      WHERE timestamp < ?
    `).run(cutoffTimestamp);
  }

  // 工具方法
  private rowToService(row: any): DeployedService {
    return {
      id: row.id,
      projectId: row.project_id,
      serverId: row.server_id,
      deploymentId: row.deployment_id,
      name: row.name,
      type: row.type,
      host: row.host,
      port: row.port || undefined,
      url: row.url,
      healthCheck: row.health_check_config ? JSON.parse(row.health_check_config) : undefined,
      status: row.status,
      version: row.version,
      deployedAt: row.deployed_at,
      lastHealthCheck: row.last_health_check || undefined,
      lastStatusChange: row.last_status_change || undefined,
      metrics: row.metrics ? JSON.parse(row.metrics) : undefined,
      metadata: row.metadata ? JSON.parse(row.metadata) : undefined,
      tags: row.tags ? JSON.parse(row.tags) : undefined
    };
  }

  close(): void {
    this.db.close();
  }
}